import React, { Fragment, useEffect, useState } from "react";
import "@/styles/pages/contat-us/contact-us.scss";
import CardComponent from "@/components/card";
import Container from "@/components/container/Container";
import { Col, DatePicker, Divider, Flex, Form, Input, Row, Select, Space } from "antd";
import { countryList } from "@/helpers/countryList";
import ButtonComponent from "@/components/button/Button";
import ContactByWhatsApp from "@/pages/contact-us/ContactByWhatsApp";
import ContactDirectly from "@/pages/contact-us/ContactDirectly";
import { setContatctUs } from "@/services/users/setContatctUs";
import { useDispatch } from "react-redux";
import { setCurrentForm, setFormProps, setModalOpen } from "@/reducers/commonSlice";
import { keyFilterOption } from "@/helpers";
import dayjs from "dayjs";

// API functions for fetching options
import { getDisciplines } from "@/services/common/getDisciplines";
import { getUniversityByCountryAndRegion } from "@/services/common/getUniversityByCountryAndRegion";

const customizeRequiredMark = (label, { required }) => (
  <>
    {label}
    {required && <span style={{ color: "red" }}>*</span>}
  </>
);

const filterOption = (input, option) =>
  (option?.label ?? "").toLowerCase().includes(input.toLowerCase());

const { Option } = Select;

const ContactUs = () => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();

  // State for controlling the conditional "Other" input for course
  const [isOtherCourseSelected, setIsOtherCourseSelected] = useState(false);
  // State for controlling the conditional "Other" input for university
  const [isOtherUniversitySelected, setIsOtherUniversitySelected] = useState(false);

  // States for fetched options
  const [courseOptions, setCourseOptions] = useState([]);
  const [universityOptions, setUniversityOptions] = useState([]);

  // State for tracking selected country (for universities)
  const [selectedCountry, setSelectedCountry] = useState(null);

  // Fetch disciplines (Preferred Course options) on mount
  useEffect(() => {
    getDisciplines()
      .then((res) => {
        const options = res.map((item) => ({
          label: item.name,
          value: item.id,
        }));
        setCourseOptions(options);
      })
      .catch((error) => console.error(error));
  }, []);

  // When a country is selected, fetch university options (without region)
  useEffect(() => {
    if (selectedCountry) {
      getUniversityByCountryAndRegion(selectedCountry, "")
        .then((res) => {
          const options = res.map((uni) => ({
            label: uni.name,
            value: uni.id,
          }));
          setUniversityOptions(options);
          form.resetFields(["university", "otherUniversity"]);
        })
        .catch((error) => console.error(error));
    }
  }, [selectedCountry, form]);

  // Handler for Preferred University changes
  const handleUniversityChange = (value) => {
    if (value === "other") {
      setIsOtherUniversitySelected(true);
    } else {
      setIsOtherUniversitySelected(false);
      form.setFieldsValue({ otherUniversity: "" });
    }
  };

  // Handler for Preferred Course changes
  const handleCourseChange = (value) => {
    if (value === "other") {
      setIsOtherCourseSelected(true);
    } else {
      setIsOtherCourseSelected(false);
      form.setFieldsValue({ otherCourse: "" });
    }
  };

  const handleCountryChange = (value) => {
    setSelectedCountry(value);
  };

  const handleFinish = (value) => {
    if (value.phoneCode) {
      value.mobileNumber = value.phoneCode + " " + value.mobileNumber;
      delete value.phoneCode;
    } else {
      value.mobileNumber = "+91 " + value.mobileNumber;
      delete value.phoneCode;
    }
    const formData = {
      name: value.name,
      email: value.email,
      mobileNumber: value.mobileNumber,
      countryCode: value.countryCode,
      // Use the custom field if "Other" was selected
      course: value.course === "other" ? value.otherCourse : value.course,
      university: value.university === "other" ? value.otherUniversity : value.university,
      location: value.location || "",
      intake: value.intake ? dayjs(value.intake).format("YYYY") : "",
    };
    setContatctUs(formData)
      .then((response) => {
        dispatch(setCurrentForm("successModal"));
        dispatch(setModalOpen(true));
        dispatch(
          setFormProps({
            message: "Your request has been registered, we will contact you soon",
            btnLabel: "Close",
            onSubmit: () => {
              dispatch(setModalOpen(false));
            },
          })
        );
        form.resetFields();
      })
      .catch((error) => {
        console.error(error);
      });
  };

  return (
    <Fragment>
      <div className="contact-us-wrapper">
        <div className="image">
          <img src="/assets/images/contact-us.png" alt="Contact us" />
        </div>
        <Container className="wrapper-form">
          <CardComponent>
            <div className="inner-card">
              <CardComponent>
                <div className="contact-us-header">
                  <div className="body-b1">Book a call now to know more about our services!</div>
                </div>

                <div className="contact-form">
                  <Form
                    name="contactUs"
                    layout="vertical"
                    form={form}
                    onFinish={handleFinish}
                    requiredMark={customizeRequiredMark}
                  >
                    <Row justify="center" gutter={[16, 0]}>
                      <Col xxl={12} xl={12} lg={12} md={12} sm={24} xs={24}>
                        <Form.Item
                          label="Name"
                          name="name"
                          rules={[
                            {
                              required: true,
                              message: "Please enter your name!",
                            },
                          ]}
                        >
                          <Input placeholder="Enter your name" />
                        </Form.Item>
                      </Col>
                      <Col xxl={12} xl={12} lg={12} md={12} sm={24} xs={24}>
                        <Form.Item
                          label="Email"
                          name="email"
                          rules={[
                            {
                              type: "email",
                              required: true,
                              message: "Please enter your email!",
                            },
                          ]}
                        >
                          <Input placeholder="Enter your email" />
                        </Form.Item>
                      </Col>
                      <Col xxl={12} xl={12} lg={12} md={12} sm={24} xs={24}>
                        <Form.Item label="Phone Number">
                          <Space.Compact style={{ width: "100%" }}>
                            <Form.Item name="phoneCode" noStyle>
                              <Select
                                virtual={false}
                                showSearch
                                placeholder="Select Country"
                                defaultValue="+91"
                                filterOption={keyFilterOption}
                                style={{ maxWidth: "35%" }}
                                dropdownRender={(menu) => (
                                  <div onWheel={(e) => e.stopPropagation()}>{menu}</div>
                                )}
                              >
                                {countryList.map((country) => (
                                  <Option
                                    key={country.name}
                                    value={country.phoneCode}
                                    label={country.phoneCode}
                                  >
                                    <Flex justify="start" align="center" gap={"5px"}>
                                      <img src={country.flag} height={30} width={30} alt="" />
                                      {window.innerWidth > 425 ? (
                                        <span>{country.phoneCode}</span>
                                      ) : null}
                                    </Flex>
                                  </Option>
                                ))}
                              </Select>
                            </Form.Item>
                            <Form.Item
                              name="mobileNumber"
                              noStyle
                              rules={[
                                {
                                  min: 7,
                                  max: 14,
                                  required: true,
                                  message: "Please provide a valid mobile number with WhatsApp.",
                                },
                              ]}
                            >
                              <Input
                                type="number"
                                style={{ width: "80%" }}
                                placeholder="Enter your mobile number"
                                onKeyPress={(event) => {
                                  if (!/[0-9]/.test(event.key)) {
                                    event.preventDefault();
                                  }
                                }}
                              />
                            </Form.Item>
                          </Space.Compact>
                        </Form.Item>
                      </Col>
                      <Col xxl={12} xl={12} lg={12} md={12} sm={24} xs={24}>
                        <Form.Item
                          label="Preferred Country"
                          name="countryCode"
                          rules={[
                            {
                              required: true,
                              message: "Please select a preferred country!",
                            },
                          ]}
                        >
                          <Select
                            virtual={false}
                            showSearch
                            placeholder="Select a Preferred Country"
                            className="w-100"
                            filterOption={filterOption}
                            onChange={handleCountryChange}
                          >
                            {countryList.map((country) => (
                              <Option
                                key={country.name}
                                value={country.isoCode}
                                label={country.name}
                              >
                                <Flex justify="start" align="center" gap={"5px"}>
                                  <img src={country.flag} height={30} width={30} alt="" />
                                  <span>{country.name}</span>
                                </Flex>
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                      {/* Preferred University */}
                      <Col xxl={12} xl={12} lg={12} md={12} sm={24} xs={24}>
                        <Form.Item
                          label="Preferred University"
                          name="university"
                          rules={[
                            {
                              required: true,
                              message: "Please select your university",
                            },
                          ]}
                        >
                          <Select
                            virtual={false}
                            placeholder="Select your university"
                            onChange={handleUniversityChange}
                            showSearch
                            filterOption={filterOption}
                          >
                            {universityOptions.map((uni) => (
                              <Option key={uni.value} value={uni.label}>
                                {uni.label}
                              </Option>
                            ))}
                            <Option value="other">Other</Option>
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col xxl={12} xl={12} lg={12} md={12} sm={24} xs={24}>
                        <Form.Item
                          label="Other University"
                          name="otherUniversity"
                          rules={[
                            {
                              required: isOtherUniversitySelected,
                              message: "Please enter your university name",
                            },
                          ]}
                        >
                          <Input
                            placeholder="Enter your university name"
                            disabled={!isOtherUniversitySelected}
                          />
                        </Form.Item>
                      </Col>
                      {/* Preferred Course */}
                      <Col xxl={12} xl={12} lg={12} md={12} sm={24} xs={24}>
                        <Form.Item
                          label="Preferred Course"
                          name="course"
                          rules={[
                            {
                              required: true,
                              message: "Please select your course",
                            },
                          ]}
                        >
                          <Select
                            virtual={false}
                            placeholder="Select your course"
                            onChange={handleCourseChange}
                            showSearch
                            filterOption={filterOption}
                          >
                            {courseOptions.map((course) => (
                              <Option key={course.value} value={course.label}>
                                {course.label}
                              </Option>
                            ))}
                            <Option value="other">Other</Option>
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col xxl={12} xl={12} lg={12} md={12} sm={24} xs={24}>
                        <Form.Item
                          label="Other Course"
                          name="otherCourse"
                          rules={[
                            {
                              required: isOtherCourseSelected,
                              message: "Please enter your course name",
                            },
                          ]}
                        >
                          <Input
                            placeholder="Enter your course name"
                            disabled={!isOtherCourseSelected}
                          />
                        </Form.Item>
                      </Col>
                      <Col xxl={12} xl={12} lg={12} md={12} sm={24} xs={24}>
                        <Form.Item label="Preferred Location" name="location">
                          <Input placeholder="Enter your location" />
                        </Form.Item>
                      </Col>
                      <Col xxl={12} xl={12} lg={12} md={12} sm={24} xs={24}>
                        <Form.Item label="Intake" name="intake">
                          <DatePicker
                            picker="year"
                            placeholder="Select the year"
                            disabledDate={(current) => {
                              const currentYear = dayjs().year();
                              return (
                                current &&
                                (current.year() < currentYear || current.year() > currentYear + 2)
                              );
                            }}
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row justify="center">
                      <Col xxl={12} xl={12} lg={12} md={12} sm={24} xs={24}>
                        <ButtonComponent
                          label="Book a Call Now!"
                          varient="primary"
                          htmlType="submit"
                          className="w-100"
                        />
                      </Col>
                    </Row>
                  </Form>
                </div>

                <Divider />

                <div className="contact-by-whatsapp-card">
                  <ContactByWhatsApp />
                </div>

                <div className="contact-directly-card">
                  <ContactDirectly />
                </div>
              </CardComponent>
            </div>
          </CardComponent>
        </Container>
      </div>
    </Fragment>
  );
};

export default ContactUs;
